/**
 * The page's own condition, in one call, for the device debug server.
 *
 * WHY THIS EXISTS (2026-09-23). Driving the built-in keyboard on the projector
 * meant asking the page the same handful of questions over and over, one `eval`
 * at a time, with a sleep between each: which element has focus, is the field
 * inert or editable, is our keyboard up and on which layer, has the watch decided
 * this device's keyboard is broken, and what does the viewport say. Assembling
 * that by hand cost minutes per step, and one of those hand-built readings was
 * reported to the owner as an app defect ("Back does not close the keyboard")
 * because the eval landed in the middle of a state change.
 *
 * So the page answers all of it at once: `GET /state` on that server evaluates
 * `window.__sliqueDebug.state()`, and `GET /players` uses `players()`. The bridge
 * is installed ONLY inside the APK (`runsOnDevice`), so the website is unchanged
 * and exposes nothing extra.
 *
 * It reports, it never decides: nothing here influences the app.
 *
 * WHAT IS DELIBERATELY NOT REPORTED. Field values are what a viewer typed, so a
 * password reports its length and nothing else, and anything that looks like a
 * URL reports its host only — the same reasoning as `DebugServer.redact` on the
 * native side, because `/bundle` archives this output to a file. Stream URLs also
 * carry the provider login in their path, which is why `players()` reports a
 * host and a size and never a source.
 */

import type { QueryClient } from "@tanstack/react-query";
import { pageBusy, type PageBusy } from "./busy.js";
import { runsOnDevice } from "../api/client.js";
import { editableFields } from "../components/keyboardOnDemand.js";
import { keyboardWatchSnapshot } from "../components/tvKeyboardWatch.js";
import { useTvKeyboard } from "../components/tvKeyboardStore.js";
import { playTrace } from "../player/playTrace.js";

type Field = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

/**
 * Duck-typed, like `keyboardOnDemand` does and for the same reason: the test
 * suite's jsdom exposes a document without publishing every element constructor
 * as a global, so `instanceof HTMLInputElement` throws there.
 */
function asField(el: Element | null): Field | null {
  const shape = el as { tagName?: string } | null;
  if (!shape) return null;
  const tag = shape.tagName;
  if (tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "SELECT") return null;
  return el as unknown as Field;
}

/** Long enough to recognise a control, short enough to read in a terminal. */
const LABEL_LIMIT = 44;
const VALUE_LIMIT = 120;

function text(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.replace(/\s+/g, " ").trim();
  return trimmed ? trimmed.slice(0, LABEL_LIMIT) : null;
}

/** A short, human name for an element: what a probe would have to guess at. */
function label(el: Element | null): string | null {
  if (!el) return null;
  const html = el as HTMLElement;
  return (
    text(html.getAttribute("aria-label")) ??
    text(html.getAttribute("placeholder")) ??
    text(html.getAttribute("data-key")) ??
    text(html.getAttribute("name")) ??
    text(html.id) ??
    text(html.tagName === "BUTTON" ? html.textContent : null)
  );
}

/** Which part of the app it sits in — the distinction that cost the most time. */
function region(el: Element | null): string {
  if (!el) return "none";
  if (el.closest(".tvkeyboard")) return "keyboard";
  if (el.closest(".topbar")) return "topbar";
  if (el.closest(".navrail")) return "rail";
  if (el.closest(".exit-confirm")) return "exit-confirm";
  if (el.closest("[data-epg-mini]")) return "epg-mini";
  if (el.closest("[data-epg-layout]")) return "guide";
  if (el.closest(".player-surface")) return "player";
  if (el.closest(".app__content")) return "content";
  return "body";
}

function describeFocus(): unknown {
  const el = document.activeElement;
  if (!el) return null;
  return {
    tag: el.tagName,
    label: label(el),
    key: (el as HTMLElement).dataset?.key ?? null,
    region: region(el),
    value: asField(el) ? safeValue(el as unknown as Field) : null,
  };
}

/**
 * What a field holds, without handing out credentials: a password is its length,
 * and a URL is its host (the path of a provider URL is the login).
 */
function safeValue(field: Field): unknown {
  const value = field.value ?? "";
  if ((field as { type?: string }).type === "password") {
    return { masked: true, valueLength: value.length };
  }
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) {
    try {
      return { host: new URL(value).host, valueLength: value.length };
    } catch {
      return { host: "unparseable", valueLength: value.length };
    }
  }
  return value.length > VALUE_LIMIT ? value.slice(0, VALUE_LIMIT) + "…" : value;
}

function describeFields(): unknown[] {
  const editable = new Set<Field>(editableFields());
  const watch = keyboardWatchSnapshot();
  // Selects belong here too: the built-in keyboard's own preference is one, and
  // "is this device set to Always?" was a question asked the long way round.
  const inputs = [...document.querySelectorAll("input, textarea, select")]
    .map((el) => asField(el))
    .filter((field): field is Field => field !== null && !field.disabled);

  // The focused field first, then a bounded list: a settings page has a dozen
  // fields behind cards, and the one being typed into must not be buried in them.
  const ordered = inputs
    .sort((a, b) => Number(b === document.activeElement) - Number(a === document.activeElement))
    .slice(0, 24);

  return ordered.map((field) => {
    const box = field.getBoundingClientRect();
    const select = field.tagName === "SELECT" ? (field as unknown as HTMLSelectElement) : null;
    return {
      label: label(field),
      type: (field as { type?: string }).type ?? (select ? "select" : "textarea"),
      // A chosen option, for the settings a device is judged on.
      selected: select ? select.value : null,
      // MARKED, not dropped. A field that is not on screen is reported as such
      // rather than omitted: a snapshot that silently leaves things out is how a
      // wrong reading gets mistaken for a missing feature.
      visible: box.width >= 1 || box.height >= 1,
      focused: field === document.activeElement,
      // `readOnly` and `inputmode` are the input rule's own hand: inert until the
      // viewer asks for it (see keyboardOnDemand). A select has neither.
      readOnly: (field as { readOnly?: boolean }).readOnly ?? false,
      inputmode: field.getAttribute("inputmode"),
      editable: editable.has(field),
      opened: watch?.openField === field,
      value: safeValue(field),
    };
  });
}

/** The keyboard panel itself, in whatever state it is in, or absent. */
function describeKeyboard(): unknown {
  const panel = document.querySelector(".tvkeyboard");
  const state = useTvKeyboard.getState();
  const focused = document.activeElement;
  const inPanel = focused instanceof HTMLElement && focused.closest(".tvkeyboard") !== null;
  const watch = keyboardWatchSnapshot();
  return {
    open: state.open,
    mode: state.mode,
    inDom: panel !== null,
    keys: panel ? panel.querySelectorAll("button").length : 0,
    // Which layer, read off the keys themselves: the symbol layer has digits.
    layer: panel ? (panel.querySelector('[data-key="1"]') ? "symbols" : "letters") : null,
    focusedKey: inPanel ? ((focused as HTMLElement).dataset?.key ?? null) : null,
    target: state.target ? label(state.target) : null,
    deviceKeyboardFailed: watch?.deviceKeyboardFailed ?? null,
  };
}

/**
 * The overlays and row counts that probes kept mis-reading (docs/tv-remote-debugging.md
 * has a section on exactly this: a guide row is a div carrying `data-guide-row`,
 * and the detail card is not a `role="dialog"`).
 */
function describeUi(): unknown {
  const count = (selector: string): number => document.querySelectorAll(selector).length;
  return {
    overlays: {
      keyboard: count(".tvkeyboard") > 0,
      epgMini: count("[data-epg-mini]") > 0,
      exitConfirm: count(".exit-confirm") > 0,
      dialogs: count('[role="dialog"]'),
      layout: document.querySelector("[data-epg-layout]")?.getAttribute("data-epg-layout") ?? null,
    },
    counts: {
      guideRows: count("[data-guide-row]"),
      homeTiles: count(".home-tile"),
      navItems: count(".navrail a"),
      videoElements: count("video"),
    },
  };
}

/**
 * Every `<video>` on the page, with the numbers that decide "audio only" and
 * "errored": `videoWidth x videoHeight` of 0 means no frames have been decoded,
 * whatever the audio is doing.
 */
export function pagePlayers(): unknown[] {
  const tiles = new Map<Element, number>();
  return [...document.querySelectorAll("video")].map((video) => {
    const surface = video.closest("[data-player-presentation], .player-surface") ?? video.parentElement;
    if (surface && !tiles.has(surface)) tiles.set(surface, tiles.size + 1);
    const error = video.error;
    const source = video.currentSrc || video.src || "";
    return {
      tile: surface ? tiles.get(surface) : null,
      region: region(video),
      paused: video.paused,
      muted: video.muted,
      readyState: video.readyState,
      size: `${video.videoWidth}x${video.videoHeight}`,
      currentTime: Math.round(video.currentTime * 10) / 10,
      bufferedAhead:
        video.buffered.length > 0
          ? Math.round((video.buffered.end(video.buffered.length - 1) - video.currentTime) * 10) / 10
          : 0,
      // `blob:` means Media Source Extensions, i.e. the page's own HLS engine; a
      // real scheme means the browser is playing the stream directly; null means
      // this element has no source, so it is holding nothing. Never the source
      // itself: a provider URL carries the login in its path.
      engine: source ? (source.startsWith("blob:") ? "mse" : "direct") : null,
      error: error ? { code: error.code, message: text(error.message) } : null,
    };
  });
}

export interface DeviceState {
  route: string;
  viewport: { height: number; visualHeight: number; keyboardOnScreen: boolean };
  focus: unknown;
  keyboard: unknown;
  fields: unknown[];
  rule: { editable: string[]; editableCount: number };
  watch: { openField: string | null; typed: boolean; pointerSeen: boolean } | null;
  ui: unknown;
  /** What the page is busy with (debug/busy.ts). */
  busy: PageBusy;
}

/** Set by installDebugBridge: the app's query client, for `busy`'s fetches in flight. */
let debugQueryClient: QueryClient | null = null;

export function deviceState(): DeviceState {
  const watch = keyboardWatchSnapshot();
  const editable = editableFields();
  return {
    route: window.location.pathname,
    viewport: {
      height: window.innerHeight,
      visualHeight: Math.round(window.visualViewport?.height ?? window.innerHeight),
      // A platform keyboard shrinks the visual viewport; ours does not (it is
      // part of the page). That difference is how "both keyboards are up" was
      // settled without a screenshot.
      keyboardOnScreen: Math.round(window.visualViewport?.height ?? window.innerHeight) < window.innerHeight - 40,
    },
    focus: describeFocus(),
    keyboard: describeKeyboard(),
    fields: describeFields(),
    rule: { editable: editable.map((field) => label(field) ?? "(unnamed field)"), editableCount: editable.length },
    watch: watch
      ? { openField: label(watch.openField as Element | null), typed: watch.typed, pointerSeen: watch.pointerSeen }
      : null,
    ui: describeUi(),
    busy: pageBusy(debugQueryClient),
  };
}

/**
 * One line about where the page is and what a press would move — small enough to
 * take before and after every key, which is what `/trace` records.
 *
 * Deliberately cheap: no field enumeration beyond what a viewer can see, no
 * viewport maths, nothing that costs a layout on a 1 GB box.
 */
export interface PageSpot {
  route: string;
  focus: string;
  region: string;
  key: string | null;
  keyboard: boolean;
  layer: string | null;
}

export function pageSpot(): PageSpot {
  const el = document.activeElement;
  const panel = document.querySelector(".tvkeyboard");
  const inPanel = el instanceof HTMLElement && el.closest(".tvkeyboard") !== null;
  return {
    route: window.location.pathname,
    focus: el ? `${el.tagName}${el.tagName === "BUTTON" ? ":" + (text((el as HTMLElement).textContent) ?? "") : ""}` : "none",
    region: region(el),
    key: inPanel ? ((el as HTMLElement).dataset?.key ?? null) : null,
    keyboard: panel !== null,
    layer: panel ? (panel.querySelector('[data-key="1"]') ? "symbols" : "letters") : null,
  };
}

/**
 * Which web build the WebView is running, and whether it is even ours: a cached
 * service worker serving an older bundle was a real half-hour of confusion
 * (2026-09-22), and the fix is to compare it with the APK's own asset.
 */
export interface PageBuild {
  scripts: string[];
  styles: string[];
  appBridge: boolean;
  serviceWorker: { controlled: boolean; script: string | null };
}

export function pageBuild(): PageBuild {
  const assets = performance
    .getEntriesByType("resource")
    .map((entry) => entry.name)
    .filter((name) => /\/assets\/[^/]+$/.test(name))
    .map((name) => name.split("/").pop() ?? name);
  const controller = navigator.serviceWorker?.controller ?? null;
  const keyboard = (globalThis as { sliqueKeyboard?: { show?: () => unknown } }).sliqueKeyboard;
  return {
    scripts: [...new Set(assets.filter((name) => name.endsWith(".js")))],
    styles: [...new Set(assets.filter((name) => name.endsWith(".css")))],
    appBridge: typeof keyboard?.show === "function",
    serviceWorker: {
      controlled: controller !== null,
      script: controller ? (controller.scriptURL.split("/").pop() ?? null) : null,
    },
  };
}

/**
 * Install the bridge the debug server calls. Returns a function that removes it.
 *
 * App only: the debug server does not exist on the website, so neither does this.
 */
export function installDebugBridge(options: { queryClient?: QueryClient } = {}): () => void {
  if (!runsOnDevice()) return () => {};
  debugQueryClient = options.queryClient ?? null;
  const bridge = {
    state: deviceState,
    busy: () => pageBusy(debugQueryClient),
    players: pagePlayers,
    spot: pageSpot,
    build: pageBuild,
    playTrace,
    at: "slique-debug-bridge/1",
  };
  (globalThis as { __sliqueDebug?: unknown }).__sliqueDebug = bridge;
  return () => {
    delete (globalThis as { __sliqueDebug?: unknown }).__sliqueDebug;
  };
}
